import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import RoomGridDashboard from './components/RoomGridDashboard';
import RoomManagement from './components/RoomManagement';
import TenantManagement from './components/TenantManagement';
import Header from './components/Header';
import UserGuide from './components/UserGuide';
import BackupRestore from './components/BackupRestore';
import SettingsModal from './components/SettingsModal';
import { type Page, type Room, type Tenant, type ResidencyRegistration, type RoomMember, type BoardingHouseInfo, type BoardingHouse, RoomStatus } from './types';
import { ROOMS_DATA, DEFAULT_BOARDING_HOUSE_INFO } from './constants';
import firebase, { onAuthState, signInWithGoogle, signOut as fbSignOut, getUserState, setUserState, isFirebaseInitialized, handleRedirectResult } from './firebase';
import type { User } from 'firebase/auth';

const LEGACY_DATA_KEY = 'ivs-staysmart-data';
const LEGACY_INFO_KEY = 'ivs-staysmart-boarding-house-info';
const HOUSES_STORAGE_KEY = 'ivs-staysmart-boarding-houses';
const API_KEY_STORAGE_KEY = 'ivs-staysmart-gemini-api-key';


const generateDefaultRooms = (count: number): Room[] => {
    return Array.from({ length: count }, (_, i) => {
        const roomNumber = (i + 1).toString().padStart(2, '0');
        return {
            id: `R${roomNumber}_${Date.now()}`,
            roomNumber: roomNumber,
            price: 0,
            status: RoomStatus.Available,
            amenities: ['A/C', 'Wi-Fi'],
            lastElectricityReading: 0,
            currentElectricityReading: 0,
        };
    });
};


const loadInitialData = (): { boardingHouses: BoardingHouse[], currentBoardingHouseIndex: number } => {
    try {
        const serializedHouses = localStorage.getItem(HOUSES_STORAGE_KEY);
        if (serializedHouses) {
            const parsed = JSON.parse(serializedHouses);
            if (Array.isArray(parsed) && parsed.length > 0) {
                 return { boardingHouses: parsed, currentBoardingHouseIndex: 0 };
            }
        }

        // Migration from old version
        const legacyDataRaw = localStorage.getItem(LEGACY_DATA_KEY);
        const legacyInfoRaw = localStorage.getItem(LEGACY_INFO_KEY);

        if (legacyDataRaw && legacyInfoRaw) {
             // informational migration log
             import('./utils/logger').then(l => l.info('Migrating data from older version...'));
             const parsedData = JSON.parse(legacyDataRaw);
             const parsedInfo = JSON.parse(legacyInfoRaw);
             
                const migratedHouse: BoardingHouse = {
                id: `BH${Date.now()}`,
                info: {
                ...DEFAULT_BOARDING_HOUSE_INFO,
                ...parsedInfo,
                numberOfRooms: (Array.isArray(parsedData.rooms) && parsedData.rooms.length > 0) ? parsedData.rooms.length : DEFAULT_BOARDING_HOUSE_INFO.numberOfRooms,
              },
                rooms: parsedData.rooms || ROOMS_DATA,
                tenants: parsedData.tenants || [],
                roomMembers: parsedData.roomMembers || [],
                residencyRegistrations: parsedData.residencyRegistrations || [],
             };
             
             // Clean up old storage keys after successful migration
             localStorage.removeItem(LEGACY_DATA_KEY);
             localStorage.removeItem(LEGACY_INFO_KEY);

             return { boardingHouses: [migratedHouse], currentBoardingHouseIndex: 0 };
        }
        
    } catch (err) {
        import('./utils/logger').then(l => l.error('Failed to load or migrate data, starting fresh.', err));
    }
    
    // Default initial state if nothing is found
    const defaultHouse: BoardingHouse = {
        id: `BH${Date.now()}`,
        info: DEFAULT_BOARDING_HOUSE_INFO,
        rooms: generateDefaultRooms(DEFAULT_BOARDING_HOUSE_INFO.numberOfRooms),
        tenants: [],
        roomMembers: [],
        residencyRegistrations: [],
    };
    return { boardingHouses: [defaultHouse], currentBoardingHouseIndex: 0 };
};


const Footer = (): JSX.Element => {
  return (
    <footer className="p-4 text-center text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
      <p>Mã D-U-N-S: 646434880</p>
      <p>© 2025 IVS JSC. Mọi quyền được bảo lưu.</p>
      <p>Được thiết kế và phát triển bởi IVS Celestech, IVS JSC.</p>
      <a href="https://ivsacademy.edu.vn/" target="_blank" rel="noopener noreferrer" className="hover:underline text-primary-600 dark:text-primary-400">
        https://ivsacademy.edu.vn/
      </a>
    </footer>
  );
};

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  const [initialData] = useState(loadInitialData);
  const [boardingHouses, setBoardingHouses] = useState<BoardingHouse[]>(initialData.boardingHouses);
  const [currentBoardingHouseIndex, setCurrentBoardingHouseIndex] = useState(initialData.currentBoardingHouseIndex);
  
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [signInLoading, setSignInLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [storageAccess, setStorageAccess] = useState<boolean | null>(null);
  const [showStorageHelp, setShowStorageHelp] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  useEffect(() => {
    const isDark = localStorage.getItem('isDarkMode') === 'true';
    setIsDarkMode(isDark);
    
    const storedApiKey = localStorage.getItem(API_KEY_STORAGE_KEY);
    if (storedApiKey) {
      setGeminiApiKey(storedApiKey);
    }
    // Attach auth listener (if Firebase configured)
    setAuthLoading(true);

    // Process redirect result (if any) before/on auth state changes to capture redirect errors
    (async () => {
      try {
        const redirectResult = await handleRedirectResult();
        if (redirectResult && redirectResult.user) {
          import('./utils/logger').then(l => l.info('Handled redirect sign-in result', redirectResult.user.uid));
        }
      } catch (err: any) {
        import('./utils/logger').then(l => l.error('Redirect sign-in error', err));
        // Provide clearer guidance to the user
        const code = err?.code ?? 'unknown_error';
        const message = err?.message ?? String(err ?? 'No error message');
        const friendly = (code === 'auth/internal-error') ? 'Đăng nhập thất bại do internal-error; kiểm tra Authorized domains và OAuth client config.' : `Redirect sign-in failed: ${code} — ${message}`;
        setAuthError(friendly + ' (Mở console để xem chi tiết.)');
        console.warn(`Redirect sign-in failed: ${code} — ${message}`);
      }
    })();



    const unsub = onAuthState(async (user) => {
      setCurrentUser(user);
      setAuthLoading(false);

      // If user signed-in, try to load remote state and merge
      if (user) {
        try {
          const server = await getUserState(user.uid);
          // read local saved state to decide merge behavior
          const localRaw = localStorage.getItem(HOUSES_STORAGE_KEY);
          let localState = null;
          if (localRaw) {
            try { localState = JSON.parse(localRaw); } catch {}
          }

          if (server && server.boardingHouses && Array.isArray(server.boardingHouses) && server.boardingHouses.length > 0) {
            // both server and local data exist -> ask user which one to keep
            if (localState && Array.isArray(localState) && localState.length > 0) {
              const useServer = window.confirm('Dữ liệu trên đám mây (Firestore) được tìm thấy. Nhấp OK để sử dụng dữ liệu trên đám mây (ghi đè dữ liệu cục bộ), hoặc Hủy để ghi đè dữ liệu trên đám mây bằng dữ liệu cục bộ.');
              if (useServer) {
                setBoardingHouses(server.boardingHouses);
                if (server.geminiApiKey) setGeminiApiKey(server.geminiApiKey);
                import('./utils/logger').then(l => l.info('Loaded user data from Firestore'));
              } else {
                await setUserState(user.uid, { boardingHouses: localState, geminiApiKey });
                import('./utils/logger').then(l => l.info('Uploaded local data to Firestore'));
                setBoardingHouses(localState);
              }
            } else {
              // only server exists
              setBoardingHouses(server.boardingHouses);
              if (server.geminiApiKey) setGeminiApiKey(server.geminiApiKey);
              import('./utils/logger').then(l => l.info('Loaded user data from Firestore'));
            }
          } else {
            // No server data: upload current local state to Firestore
            if (localState && Array.isArray(localState) && localState.length > 0) {
              await setUserState(user.uid, { boardingHouses: localState, geminiApiKey });
              import('./utils/logger').then(l => l.info('Created initial user state in Firestore'));
            } else if (boardingHouses && boardingHouses.length > 0) {
              // fallback: server empty, local state variable in memory
              await setUserState(user.uid, { boardingHouses, geminiApiKey });
              import('./utils/logger').then(l => l.info('Created initial user state in Firestore'));
            }
          }
        } catch (err) {
          import('./utils/logger').then(l => l.error('Error syncing initial user state:', err));
        }
      }
    });

    return () => unsub && unsub();
  }, []);

  // Detect whether the browser allows storage (cookies/localStorage) for this page.
  useEffect(() => {
    const checkStorage = async () => {
      // Prefer the standard hasStorageAccess API when available
      if (typeof (document as any).hasStorageAccess === 'function') {
        try {
          const allowed = await (document as any).hasStorageAccess();
          setStorageAccess(!!allowed);
          if (!allowed) {
            console.warn('Storage access blocked by browser. Some features may not work properly.');
          }
          return;
        } catch (e) {
          // ignore and fall back
        }
      }

      // Fallback: try writing to localStorage
      try {
        localStorage.setItem('__storage_test__', '1');
        localStorage.removeItem('__storage_test__');
        setStorageAccess(true);
      } catch (e) {
        console.warn('LocalStorage access blocked. Some features may not work properly.');
        setStorageAccess(false);
      }
    };
    checkStorage();
  }, []);

  const recheckStorage = async () => {
    setStorageAccess(null);
    try {
      if (typeof (document as any).hasStorageAccess === 'function') {
        const allowed = await (document as any).hasStorageAccess();
        setStorageAccess(!!allowed);
        return allowed;
      }
    } catch (e) { /* fallthrough */ }
    try { localStorage.setItem('__storage_test__','1'); localStorage.removeItem('__storage_test__'); setStorageAccess(true); return true; } catch (e) { setStorageAccess(false); return false; }
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('isDarkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('isDarkMode', 'false');
    }
  }, [isDarkMode]);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    try {
      const serializedState = JSON.stringify(boardingHouses);
      localStorage.setItem(HOUSES_STORAGE_KEY, serializedState);
      import('./utils/logger').then(l => l.debug('Boarding house data saved successfully.'));
    } catch (err: any) {
      import('./utils/logger').then(l => l.error('Could not save state to localStorage:', err));
    }
  }, [boardingHouses]);

  // Persist state to Firestore when a user is logged in
  useEffect(() => {
    const persistForUser = async () => {
      if (!currentUser) return;
      try {
        await setUserState(currentUser.uid, { boardingHouses, geminiApiKey });
        import('./utils/logger').then(l => l.debug('Saved user state to Firestore'));
      } catch (err) {
        import('./utils/logger').then(l => l.error('Could not save state to Firestore:', err));
      }
    };
    persistForUser();
  }, [boardingHouses, geminiApiKey, currentUser]);
  
  useEffect(() => {
    try {
      localStorage.setItem(API_KEY_STORAGE_KEY, geminiApiKey);
      import('./utils/logger').then(l => l.debug('API Key saved successfully.'));
    } catch (err: any) {
      import('./utils/logger').then(l => l.error('Could not save API key to localStorage:', err));
    }
  }, [geminiApiKey]);
  
  const handleSettingsUpdate = (settings: { apiKey: string, updatedInfo: BoardingHouseInfo, houseIndex: number }) => {
    const { apiKey, updatedInfo, houseIndex } = settings;
    setGeminiApiKey(apiKey);

    // Auto-map landlord fields to host fields for contract usage
    const mappedInfo: BoardingHouseInfo = {
      ...updatedInfo,
      // Map landlord info to host info for contracts
      hostName: updatedInfo.landlordName || updatedInfo.hostName,
      hostBirthDate: updatedInfo.landlordDob || updatedInfo.hostBirthDate,
      hostIdNumber: updatedInfo.landlordIdNumber || updatedInfo.hostIdNumber,
      // Keep existing host fields if they were already set
      hostIdIssueDate: updatedInfo.hostIdIssueDate,
      hostIdIssuePlace: updatedInfo.hostIdIssuePlace,
      hostPermanentAddress: updatedInfo.hostPermanentAddress,
    };

    // Debug: Log the mapping process
    console.log('Settings Update - Original info:', updatedInfo);
    console.log('Settings Update - Mapped info:', mappedInfo);

    const targetHouse = boardingHouses[houseIndex];
    const originalRoomCount = targetHouse.info.numberOfRooms;
    const newRoomCount = mappedInfo.numberOfRooms;
    
    let updatedRooms = targetHouse.rooms;

    if (newRoomCount > originalRoomCount) {
        const roomsToAddCount = newRoomCount - originalRoomCount;
        const newRooms: Room[] = [];
        const existingRoomNumbers = new Set(targetHouse.rooms.map(r => r.roomNumber));
        let nextRoomNum = 1;
        for (let i = 0; i < roomsToAddCount; i++) {
            while (existingRoomNumbers.has(nextRoomNum.toString().padStart(2, '0'))) {
                nextRoomNum++;
            }
            const roomNumberStr = nextRoomNum.toString().padStart(2, '0');
            newRooms.push({
                id: `R${roomNumberStr}_${Date.now()}`,
                roomNumber: roomNumberStr,
                price: 0, status: RoomStatus.Available, amenities: ['A/C', 'Wi-Fi'],
                lastElectricityReading: 0, currentElectricityReading: 0,
            });
            existingRoomNumbers.add(roomNumberStr);
            nextRoomNum++;
        }
        updatedRooms = [...updatedRooms, ...newRooms];
    } else if (newRoomCount < originalRoomCount) {
        const roomsToRemoveCount = originalRoomCount - newRoomCount;
        const unoccupiedRooms = targetHouse.rooms.filter(r => r.status !== RoomStatus.Occupied);
        
        if (unoccupiedRooms.length < roomsToRemoveCount) {
            alert(`Không thể giảm số lượng phòng. Cần có ít nhất ${roomsToRemoveCount} phòng trống hoặc đang bảo trì. Hiện tại chỉ có ${unoccupiedRooms.length}.`);
            return; // Abort the save
        }

        const sortedUnoccupied = unoccupiedRooms.sort((a, b) => b.roomNumber.localeCompare(a.roomNumber));
        const roomIdsToRemove = new Set(sortedUnoccupied.slice(0, roomsToRemoveCount).map(r => r.id));
        updatedRooms = targetHouse.rooms.filter(r => !roomIdsToRemove.has(r.id));
    }
    
    setBoardingHouses(prev => {
        const newHouses = [...prev];
        newHouses[houseIndex] = {
            ...newHouses[houseIndex],
            info: mappedInfo,
            rooms: updatedRooms
        };
        return newHouses;
    });
};

  const handleAddBoardingHouse = () => {
    const newHouse: BoardingHouse = {
      id: `BH${Date.now()}`,
      info: { ...DEFAULT_BOARDING_HOUSE_INFO },
      rooms: generateDefaultRooms(DEFAULT_BOARDING_HOUSE_INFO.numberOfRooms),
      tenants: [],
      roomMembers: [],
      residencyRegistrations: [],
    };
    setBoardingHouses(prev => {
      const newHouses = [...prev, newHouse];
      // switch to the newly added boarding house (safe: derive index from previous length)
      setCurrentBoardingHouseIndex(newHouses.length - 1);
      return newHouses;
    });
  };

  const handleSignIn = async () => {
    if (!isFirebaseInitialized()) return alert('Firebase is not configured. Please set VITE_FIREBASE_... env vars.');
    setSignInLoading(true);
    setAuthError(null);
    try {
      const res = await signInWithGoogle();
      // If res is undefined and redirect was triggered, the page will navigate away.
      if (!res) {
        // redirect flow started
        import('./utils/logger').then(l => l.info('Redirect sign-in initiated'));
        return;
      }
    } catch (err: any) {
      import('./utils/logger').then(l => l.error('Sign-in failed', err));
      const code = err?.code ?? 'unknown_error';
      const message = err?.message ?? String(err ?? 'No error message');

      // Map common errors to actionable messages
      let friendly = `Đăng nhập thất bại (${code}). `;
      if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user') {
        friendly += 'Trình duyệt đã chặn popup hoặc bạn đã đóng popup. Hệ thống sẽ thử lại bằng cách chuyển hướng (redirect). Vui lòng vô hiệu hóa chặn cửa sổ bật lên hoặc thử lại.';
      } else if (code === 'auth/internal-error' || code === 'auth/operation-not-supported-in-this-environment') {
        friendly += 'Có vẻ môi trường hiện tại chặn popups/third-party cookies hoặc cấu hình OAuth chưa hoàn chỉnh. Kiểm tra Google provider trong Firebase Console và Authorized domains.';
      } else {
        friendly += message;
      }

      // Surface persistent error on UI
      setAuthError(friendly + ' (Mở console để xem chi tiết.)');
      // Also show immediate alert so user notices
      alert(friendly + `\n\nChi tiết: ${message}`);
    } finally {
      setSignInLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await fbSignOut();
      setCurrentUser(null);
      import('./utils/logger').then(l => l.info('User signed out'));
    } catch (err) {
      import('./utils/logger').then(l => l.error('Sign out failed', err));
    }
  };

  // Resolve current boarding house safely without causing state changes during render.
  const currentBH = useMemo(() => {
    return boardingHouses[currentBoardingHouseIndex] ?? boardingHouses[0] ?? null;
  }, [boardingHouses, currentBoardingHouseIndex]);

  // Ensure selected index stays in range if boarding houses change.
  useEffect(() => {
    if (boardingHouses.length === 0) {
      setCurrentBoardingHouseIndex(0);
    } else if (currentBoardingHouseIndex >= boardingHouses.length) {
      // If currentIndex is out of bounds (e.g. house removed), reset to 0.
      setCurrentBoardingHouseIndex(0);
    }
  }, [boardingHouses, currentBoardingHouseIndex]);

  const allPeople = useMemo(() => {
    if (!currentBH) return [];
    const peopleMap = new Map();
    const addPerson = (p: Tenant | RoomMember) => {
        if (p.idNumber && p.idNumber.trim() !== '') {
            if (!peopleMap.has(p.idNumber)) {
                peopleMap.set(p.idNumber, {
                    name: p.name, gender: p.gender, idType: p.idType, idNumber: p.idNumber,
                    phone: p.phone, permanentAddress: p.permanentAddress, dateOfBirth: p.dateOfBirth,
                    idIssueDate: p.idIssueDate,
                });
            }
        }
    };
    currentBH.tenants.forEach(addPerson);
    currentBH.roomMembers.forEach(addPerson);
    return Array.from(peopleMap.values());
  }, [currentBH]);

  const updateCurrentBH = useCallback((updater: (bh: BoardingHouse) => BoardingHouse) => {
    setBoardingHouses(prev => {
        return prev.map((bh, index) => index === currentBoardingHouseIndex ? updater(bh) : bh);
    });
  }, [currentBoardingHouseIndex]);

  // Stabilized state setters for child components to prevent re-renders
  const setRooms = useCallback((action: React.SetStateAction<Room[]>) => {
    updateCurrentBH(bh => ({ ...bh, rooms: typeof action === 'function' ? action(bh.rooms) : action }));
  }, [updateCurrentBH]);

  const setTenants = useCallback((action: React.SetStateAction<Tenant[]>) => {
    updateCurrentBH(bh => ({ ...bh, tenants: typeof action === 'function' ? action(bh.tenants) : action }));
  }, [updateCurrentBH]);

  const setRoomMembers = useCallback((action: React.SetStateAction<RoomMember[]>) => {
    updateCurrentBH(bh => ({ ...bh, roomMembers: typeof action === 'function' ? action(bh.roomMembers) : action }));
  }, [updateCurrentBH]);

  const setResidencyRegistrations = useCallback((action: React.SetStateAction<ResidencyRegistration[]>) => {
    updateCurrentBH(bh => ({ ...bh, residencyRegistrations: typeof action === 'function' ? action(bh.residencyRegistrations) : action }));
  }, [updateCurrentBH]);


  if (!currentBH) {
    return <div className="flex h-screen w-full items-center justify-center bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">Đang tải dữ liệu...</div>;
  }
  
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-200">
      <Sidebar 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage} 
        boardingHouseInfo={currentBH.info}
        onOpenSettings={() => setIsSettingsModalOpen(true)}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          currentPage={currentPage} 
          isDarkMode={isDarkMode} 
          toggleDarkMode={() => setIsDarkMode(prev => !prev)}
          onOpenSettings={() => setIsSettingsModalOpen(true)}
          currentUser={currentUser}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          signInLoading={signInLoading}
        />
        {authError && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-3 m-4 rounded">
            <strong>Vấn đề đăng nhập:</strong> {authError}
            <div className="text-sm mt-1">Kiểm tra <em>Firebase Console → Authentication → Sign-in method</em> và <em>Authorized domains</em>, hoặc thử lại ở chế độ ẩn danh (Incognito).</div>
          </div>
        )}

        {/* Storage/tracking-prevention detection banner */}
        {storageAccess === false && (
          <div className="bg-red-50 border-l-4 border-red-400 text-red-800 p-3 m-4 rounded">
            <strong>Trình duyệt đang chặn lưu trữ</strong>
            <p className="text-sm mt-1">Trình duyệt của bạn đang chặn cookies hoặc lưu trữ bên thứ 3 (Tracking Prevention), điều này có thể ngăn Google Sign-in hoạt động. Vui lòng cho phép cookies/cho phép trang này hoặc thử mở ở chế độ ẩn danh (Incognito).</p>
            <div className="mt-3 flex gap-2">
              <button onClick={() => setShowStorageHelp(true)} className="px-3 py-1 bg-white border rounded">Xem cách bật</button>
              <button onClick={() => { recheckStorage(); }} className="px-3 py-1 bg-white border rounded">Kiểm tra lại</button>
            </div>
          </div>
        )}

        {showStorageHelp && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl p-6">
              <div className="flex justify-between items-start">
                <h2 className="text-lg font-semibold">Cách bật cookies / tắt Tracking Prevention</h2>
                <button onClick={() => setShowStorageHelp(false)} className="text-gray-500 hover:text-gray-700">&times;</button>
              </div>
              <div className="mt-4 text-sm space-y-3">
                <p><strong>Chrome / Edge:</strong> Cài đặt → Privacy and security → Cookies and other site data → Allow sites to save and read cookie data, hoặc thêm trang vào phần "Sites that can always use cookies".</p>
                <p><strong>Firefox:</strong> Click biểu tượng lá chắn bên trái thanh địa chỉ → Turn off Enhanced Tracking Protection for this site.</p>
                <p><strong>Safari (macOS/iOS):</strong> Preferences → Privacy → untick "Prevent cross-site tracking" (macOS) hoặc bật cookies trong Settings → Safari trên iOS.</p>
                <p>Hoặc thử mở trang trong cửa sổ ẩn danh (Incognito) nếu bạn không muốn thay đổi thiết lập trình duyệt hiện tại.</p>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={async () => { await recheckStorage(); }} className="px-3 py-1 bg-primary-600 text-white rounded">Kiểm tra lại</button>
                <button onClick={() => setShowStorageHelp(false)} className="px-3 py-1 border rounded">Đóng</button>
              </div>
            </div>
          </div>
        )}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-800 p-4 sm:p-6 lg:p-8">
          {currentPage === 'dashboard' && <RoomGridDashboard 
              rooms={currentBH.rooms} 
              setRooms={setRooms}
              tenants={currentBH.tenants}
              boardingHouseInfo={currentBH.info}
           />}
          {currentPage === 'statistics' && <Dashboard rooms={currentBH.rooms} tenants={currentBH.tenants} />}
          {currentPage === 'rooms' && <RoomManagement 
              rooms={currentBH.rooms} 
              setRooms={setRooms}
              tenants={currentBH.tenants}
              setTenants={setTenants}
              roomMembers={currentBH.roomMembers}
              setRoomMembers={setRoomMembers}
              residencyRegistrations={currentBH.residencyRegistrations}
              setResidencyRegistrations={setResidencyRegistrations}
              allPeople={allPeople}
              geminiApiKey={geminiApiKey}
              boardingHouseInfo={currentBH.info}
           />}
          {currentPage === 'tenants' && <TenantManagement
              tenants={currentBH.tenants}
              setTenants={setTenants}
              rooms={currentBH.rooms}
              setRooms={setRooms}
              roomMembers={currentBH.roomMembers}
              setRoomMembers={setRoomMembers}
              residencyRegistrations={currentBH.residencyRegistrations}
              setResidencyRegistrations={setResidencyRegistrations}
              allPeople={allPeople}
              geminiApiKey={geminiApiKey}
              boardingHouseInfo={currentBH.info}
          />}
          {currentPage === 'guide' && <UserGuide />}
          {currentPage === 'backup' && <BackupRestore
              boardingHouses={boardingHouses}
              setBoardingHouses={setBoardingHouses}
              geminiApiKey={geminiApiKey}
              setGeminiApiKey={setGeminiApiKey}
          />}
        </main>
        <Footer />
      </div>
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onSave={handleSettingsUpdate}
        currentApiKey={geminiApiKey}
        boardingHouses={boardingHouses}
        currentBoardingHouseIndex={currentBoardingHouseIndex}
        onHouseChange={setCurrentBoardingHouseIndex}
        onHouseAdd={handleAddBoardingHouse}
      />
    </div>
  );
};

export default App;